import { useState, useEffect, useCallback } from "react"
import { Users, LayoutDashboard, ChevronDown, ChevronRight, Loader2, Trash2, LogOut, Menu, X } from "lucide-react"
import { Link, navigate } from "../lib/router"
import { deleteAccount } from "../lib/api"
import DeleteAccountModal from "../components/DeleteAccountModal"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function ClubAdminMyClub() {
  const role = "Club Admin"
  const clubName = localStorage.getItem("clubName") || "My Club"
  const userName = localStorage.getItem("name") || ""
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = () => setMenuOpen(false)
  const { t } = useLanguage()

  const [roster, setRoster] = useState(null)
  const [rosterLoading, setRosterLoading] = useState(true)
  const [expandedCoaches, setExpandedCoaches] = useState({})
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const fetchRoster = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/club/roster`, {
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      })
      if (res.ok) {
        setRoster(await res.json())
      } else {
        console.error("Roster fetch failed", res.status)
        setRoster(null)
      }
    } catch (e) {
      console.error("Roster fetch failed", e)
      setRoster(null)
    } finally {
      setRosterLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchRoster()
  }, [fetchRoster])

  const toggleCoach = (coachId) => {
    setExpandedCoaches((prev) => ({ ...prev, [coachId]: !prev[coachId] }))
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    if (await deleteAccount()) {
      localStorage.clear()
      navigate("/login")
    } else {
      setDeleting(false)
      setDeleteOpen(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <div>
        {/* Mobile top bar */}
        <div className="flex items-center justify-between border-b border-white/10 bg-ink-800/80 px-4 py-3 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/vietnam_aquatics.png" alt="Logo" className="h-7 w-auto rounded-md bg-white/95 p-1" onError={(e) => { e.currentTarget.style.display = "none" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400">{role}</span>
          </div>
          <button onClick={() => setMenuOpen(true)} aria-label="Open menu" className="flex h-9 w-9 items-center justify-center rounded border border-white/15 text-paper-0">
            <Menu className="h-5 w-5" />
          </button>
        </div>

        {/* Mobile backdrop */}
        {menuOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={closeMenu} aria-hidden="true" />}

        <aside
          className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shrink-0 flex-col overflow-y-auto border-r border-white/10 bg-ink-800 p-6 transition-transform duration-300 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-64 lg:max-w-none lg:translate-x-0 lg:bg-ink-800/50 ${
            menuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <button onClick={closeMenu} aria-label="Close menu" className="mb-6 flex h-11 w-11 items-center justify-center self-end text-mist-300 lg:hidden">
            <X className="h-5 w-5" />
          </button>

          <div className="mb-10 flex flex-col items-center gap-4 text-center">
            <img
              src="/vietnam_aquatics.png"
              alt="Logo"
              className="h-16 w-auto rounded-xl bg-white/95 p-2"
              onError={(e) => { e.currentTarget.style.display = "none" }}
            />
            <div className="flex flex-col items-center gap-1.5 mt-2">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold-400">{role}</span>
              {clubName && <span className="text-2xl font-bold text-paper-0">{clubName}</span>}
              {userName && <span className={`font-bold tracking-tight ${clubName ? 'text-base text-mist-300' : 'text-2xl text-paper-0'}`}>{userName}</span>}
            </div>
          </div>

          <nav className="flex flex-col gap-2 flex-1">
            <Link to="/club-admin/dashboard" onClick={closeMenu} className="flex items-center gap-3 rounded px-4 py-2.5 text-base font-medium text-mist-300 transition-colors hover:bg-white/5 hover:text-paper-0">
              <LayoutDashboard className="h-5 w-5" />
              {t("nav_dashboard")}
            </Link>
            <Link to="/club-admin/my-club" onClick={closeMenu} className="flex items-center gap-3 rounded bg-gold-500/10 px-4 py-2.5 text-base font-semibold text-gold-400">
              <Users className="h-5 w-5" />
              {t("nav_my_club")}
            </Link>
          </nav>

          <div className="mt-auto border-t border-white/10 pt-6">
            <Link to="/login" onClick={() => { localStorage.clear(); closeMenu() }} className="group flex w-full items-center gap-3 rounded border border-red-500/20 bg-red-500/5 px-4 py-2.5 text-base font-semibold text-red-400 transition-all duration-300 hover:border-red-500/40 hover:bg-red-500 hover:text-white hover:shadow-[0_0_15px_rgba(239,68,68,0.25)]">
              <div className="flex items-center justify-center transition-transform group-hover:-translate-x-1">
                <LogOut className="h-5 w-5" />
              </div>
              {t("nav_sign_out")}
            </Link>
            <button
              onClick={() => setDeleteOpen(true)}
              className="mt-2 flex w-full items-center gap-3 rounded px-4 py-2.5 text-base font-medium text-mist-500 transition-colors hover:text-red-400"
            >
              <Trash2 className="h-5 w-5" />
              {t("nav_delete_account")}
            </button>
          </div>
        </aside>
      </div>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteAccount}
        busy={deleting}
      />

      <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-y-auto">
        <header className="mb-10">
          <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("club_admin_my_club_title")}</h1>
          <p className="mt-1 text-sm text-mist-300">
            {roster?.club?.name ? `${roster.club.name} — ` : ""}{t("club_admin_my_club_subtitle")}
          </p>
        </header>

        <div className="rounded border border-white/10 bg-ink-800/30">
          <div className="flex items-center gap-3 border-b border-white/10 p-5">
            <Users className="h-5 w-5 text-gold-400" />
            <h2 className="font-display text-lg font-bold text-paper-0">
              {t("club_admin_my_club_coaches")}
            </h2>
            {!rosterLoading && roster && (
              <span className="ml-auto text-sm text-mist-400">
                {roster.coaches.length} {t("club_admin_my_club_coach_count")}
              </span>
            )}
          </div>

          <div className="p-5">
            {rosterLoading ? (
              <div className="flex items-center gap-2 text-sm text-mist-300">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t("club_admin_my_club_loading")}
              </div>
            ) : !roster || roster.coaches.length === 0 ? (
              <p className="text-mist-400 text-sm">{t("club_admin_my_club_no_coaches")}</p>
            ) : (
              <div className="flex flex-col gap-2">
                {roster.coaches.map(coach => {
                  const isOpen = !!expandedCoaches[coach.id]
                  const swimmerCount = coach.swimmers.length
                  return (
                    <div key={coach.id} className="overflow-hidden rounded border border-white/10 bg-ink-800/50">
                      <button
                        onClick={() => toggleCoach(coach.id)}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5"
                      >
                        {isOpen
                          ? <ChevronDown className="h-4 w-4 shrink-0 text-gold-400" />
                          : <ChevronRight className="h-4 w-4 shrink-0 text-gold-400" />}
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-ink-700 text-xs font-bold text-gold-400">
                          {(coach.name || "?").slice(0, 2).toUpperCase()}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-paper-0">{coach.name}</p>
                          <p className="truncate text-xs text-mist-400">{coach.email}</p>
                        </div>
                        <span className="flex shrink-0 items-center gap-1.5 text-xs text-mist-300">
                          <Users className="h-3.5 w-3.5" />
                          {swimmerCount} {t("club_admin_my_club_swimmer_count")}
                        </span>
                      </button>

                      {isOpen && (
                        <div className="border-t border-white/10 bg-ink-900/40 px-4 py-3">
                          {swimmerCount === 0 ? (
                            <p className="text-sm text-mist-400">{t("club_admin_my_club_no_swimmers")}</p>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {coach.swimmers.map(swimmer => (
                                <div key={swimmer.id} className="flex items-center gap-3 rounded border border-white/5 bg-ink-800/60 px-3 py-2">
                                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/10 bg-ink-700 text-[10px] font-bold text-mist-300">
                                    {(swimmer.name || "?").slice(0, 2).toUpperCase()}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-paper-0">{swimmer.name}</p>
                                    <p className="text-xs text-mist-400">
                                      {[swimmer.swimmer_code, swimmer.age_group, swimmer.dob]
                                        .filter(Boolean)
                                        .join(" · ") || t("club_admin_my_club_no_details")}
                                    </p>
                                  </div>
                                  {swimmer.status === "approved" && (
                                    <span className="shrink-0 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                                      {t("club_admin_my_club_approved")}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}